import React, { createContext, useCallback, useContext, useMemo, useReducer } from 'react';
import { createPortal } from 'react-dom';
import { Toast, ToastKind } from './Toast';

export interface ShowToastOptions {
  kind?: ToastKind;
  durationMs?: number;
}

interface ToastItem {
  id: string;
  kind: ToastKind;
  message: string;
  durationMs: number;
}

interface ToastContextValue {
  showToast: (message: string, opts?: ShowToastOptions) => void;
}

const MAX_STACK = 3;
const DEFAULT_DURATION_MS = 4000;

const ToastContext = createContext<ToastContextValue | null>(null);

type Action =
  | { type: 'add'; item: ToastItem }
  | { type: 'remove'; id: string };

function reducer(state: ToastItem[], action: Action): ToastItem[] {
  switch (action.type) {
    case 'add': {
      const next = [...state, action.item];
      // max 3 件 stack (古い順に drop)
      if (next.length > MAX_STACK) {
        return next.slice(next.length - MAX_STACK);
      }
      return next;
    }
    case 'remove':
      return state.filter((t) => t.id !== action.id);
    default:
      return state;
  }
}

let toastIdCounter = 0;
function nextId(): string {
  toastIdCounter += 1;
  return `toast-${Date.now()}-${toastIdCounter}`;
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, dispatch] = useReducer(reducer, []);

  const showToast = useCallback((message: string, opts?: ShowToastOptions) => {
    const item: ToastItem = {
      id: nextId(),
      kind: opts?.kind ?? 'info',
      message,
      durationMs: opts?.durationMs ?? DEFAULT_DURATION_MS,
    };
    dispatch({ type: 'add', item });
  }, []);

  const handleDismiss = useCallback((id: string) => {
    dispatch({ type: 'remove', id });
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <div
            className="pointer-events-none fixed top-4 right-4 z-[60] flex flex-col gap-2"
            data-testid="toast-portal"
          >
            {toasts.map((t) => (
              <Toast
                key={t.id}
                id={t.id}
                kind={t.kind}
                message={t.message}
                durationMs={t.durationMs}
                onDismiss={handleDismiss}
              />
            ))}
          </div>,
          document.body
        )}
    </ToastContext.Provider>
  );
};

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast() must be used within <ToastProvider>');
  }
  return ctx;
}
